import { useState } from 'react';
import styles from './MFASettings.module.css';
import { Toggle } from 'rsuite';
import { Spinner } from '../../UI/Spinner/Spinner';
import { useModal } from '../../hooks/modalContext';
import { useMFA } from '../../hooks/mfaListContext';
import { getTenantDetails } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { Mail, PhoneIcon, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import ClickAwayListener from 'react-click-away-listener';
import AddPhoneNumber from '../../../pages/AddPhoneNumber/AddPhoneNumber';
import VerificationBeforeMFA from '../VerificationMFA/VerificationMFA';
import { InlineAlert } from '../../UI/InlineAlert/InlineAlert';

const MFASettings = () => {
  const { setModal } = useModal();
  const { emailEnabled, phoneEnabled, phoneMfaId, emailMfaId, loadingMfa } = useMFA();
  const [verifyPhone, setVerifyPhone] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNumber, setShowNumber] = useState(false);
  const { auth } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  const addPhoneMFA = async () => {
    try {
      setLoading(true);
      const response = await getTenantDetails(auth?.data.token);
      if (response.data.user.phoneNumber) {
        setVerifyPhone(true);
      } else {
        setShowNumber(true);
      }
    } catch {
      console.error('Error fetching tenant details.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingMfa || loading) {
    return <Spinner />;
  }
  return (
    <motion.main
      className={styles.main}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <span> Two-factor authentication settings</span>{' '}
        <X className={styles.crossIcon} size={24} onClick={() => setModal(null)} stroke='#0E0E0B' />
      </div>
      <div className={styles.containerOuter}>
        <div className={styles.methodsOuter}>
          {serverError && (
            <InlineAlert
              type='error'
              message={serverError}
              dismissible
              onDismiss={() => setServerError('')}
            />
          )}
          {/* Email MFA Box */}
          <div
            className={`${styles.methodOuter} ${styles.methodOuterLight}`}
            onClick={() => {
              if (!emailEnabled) {
                setVerifyEmail(true);
              } else if (phoneEnabled) {
                setModal('remove-mfa', { isEmailVerification: true, mfaId: emailMfaId });
              } else {
                setServerError(
                  'You cannot disable Email Authentication as Phone Authentication is already disabled. One method must remain enabled'
                );
              }
            }}
          >
            <Mail stroke='#0E0E0B' />
            <p className={`${styles.textLabel}`}>Email authentication</p>
            <p className={styles.content}>
              Enable email authentication to receive a verification code via email for enhanced
              security.
            </p>
            <Toggle checked={emailEnabled} color={emailEnabled ? 'green' : undefined} />
          </div>

          {/* Phone MFA Box */}
          <div
            className={`${styles.methodOuter} ${styles.methodOuterLight} `}
            onClick={() => {
              if (!phoneEnabled) {
                addPhoneMFA();
              } else if (emailEnabled) {
                setModal('remove-mfa', { isPhoneVerification: true, mfaId: phoneMfaId });
              } else {
                setServerError(
                  'You cannot disable Phone Authentication as Email Authentication is already disabled. One method must remain enabled'
                );
              }
            }}
          >
            <PhoneIcon stroke='#0E0E0B' />
            <p className={`${styles.textLabel}`}>Phone authentication</p>
            <p className={styles.content}>
              Enable phone authentication to receive a verification code via SMS for an extra layer
              of protection.
            </p>
            <Toggle checked={phoneEnabled} color={phoneEnabled ? 'green' : undefined} />
          </div>
        </div>
      </div>
      {(showNumber || verifyPhone || verifyEmail) && (
        <div className='fixed inset-0 bg-black/40 z-[98]' />
      )}

      <AnimatePresence mode='wait' custom={'add-phone'}>
        {showNumber && (
          <ClickAwayListener onClickAway={() => setShowNumber(false)}>
            <motion.div
              className='flex w-full  absolute bottom-0 left-0 border-[2px]  rounded-[12px] z-[99]'
              initial={{ y: 300, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 300, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <AddPhoneNumber
                setNumber={setShowNumber}
                setVerifyPhone={setVerifyPhone}
                onClose={() => setShowNumber(false)}
              />
            </motion.div>
          </ClickAwayListener>
        )}
      </AnimatePresence>
      <AnimatePresence mode='wait' custom={'verify-phone'}>
        {verifyPhone && (
          <ClickAwayListener onClickAway={() => setVerifyPhone(false)}>
            <motion.div
              className='flex w-full  absolute bottom-0 left-0 border-[2px]  rounded-[12px] z-[99]'
              initial={{ y: 300, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 300, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <VerificationBeforeMFA
                verificationType='phone'
                onClose={() => setVerifyPhone(false)}
              />
            </motion.div>
          </ClickAwayListener>
        )}
      </AnimatePresence>
      <AnimatePresence mode='wait' custom={'verify-email'}>
        {verifyEmail && (
          <ClickAwayListener onClickAway={() => setVerifyEmail(false)}>
            <motion.div
              className='flex w-full  absolute bottom-0 left-0 border-[2px]  rounded-[12px] z-[99]'
              initial={{ y: 300, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 300, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <VerificationBeforeMFA
                verificationType='email'
                onClose={() => setVerifyEmail(false)}
              />
            </motion.div>
          </ClickAwayListener>
        )}
      </AnimatePresence>
    </motion.main>
  );
};

export default MFASettings;
